/**
 * Restaurant / F&B services
 * Handles restaurant tables, FB orders, table reservations, table status, KOT, production.
 */
import axios from "axios";
import db from "../database";
import useAuthStore from "../stores/useAuthStore";
import { applyAuthInterceptors } from "./authInterceptor";
import { applyHealthInterceptors } from "../utils/healthReporter";

const retailURL = process.env.REACT_APP_serverUrl;
const retailInstance = axios.create();
retailInstance.defaults.baseURL = retailURL;
retailInstance.defaults.method = "POST";
retailInstance.defaults.headers.post["Content-Type"] = "application/json";

// Dynamic tenant baseURL interceptor
retailInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_serverUrl;
  if (url) config.baseURL = url;
  return config;
});

// Auth interceptors: token attachment + 401 refresh/retry
applyAuthInterceptors(retailInstance);

// Health tracking interceptors (latency + success/failure)
applyHealthInterceptors(retailInstance);

const syncRestaurantTables = async () => {
  try {
    const tillData = useAuthStore.getState().tillData;
    const tillId = tillData?.tillAccess?.cwrTill?.cwrTillID;

    if (!tillId) throw new Error("Till ID is not available");

    const getRestaurantTablesData = await fetchRestaurantTables(tillId);
    if (!getRestaurantTablesData || getRestaurantTablesData?.length === 0) {
      // No tables found — continue gracefully
    }

    const { tempData, sectionData } = processRestaurantTables(getRestaurantTablesData);

    await db.sectionTables.clear();
    await db.sectionTables.bulkAdd(sectionData);

    const occupiedData = await fetchOccupiedTablesData();

    if (Array.isArray(occupiedData) && occupiedData.length > 0) {
      updateTableDataWithOccupiedInfo(tempData, occupiedData);
    }

    // For any table the server returned as OPN, decide whether to trust the server
    // or preserve local state based on two rules:
    //   Rule 1 — getTableStatus was empty: cannot trust an empty response (network blip /
    //             API error). Preserve ALL local non-OPN tables regardless of sync state.
    //   Rule 2 — getTableStatus had data: trust the server for tables whose last
    //             upsertTableStatus was confirmed (tabbleSync: "Y"). Only preserve tables
    //             with a pending unconfirmed status change (tabbleSync: "N") — these are
    //             tables whose API call failed and are awaiting retry.
    // This ensures multi-terminal correctness: a table freed on another terminal
    // (tabbleSync "Y" locally) correctly shows as OPN after sync.
    const isGetTableStatusEmpty = !Array.isArray(occupiedData) || occupiedData.length === 0;
    console.log("[BUG-1499] isGetTableStatusEmpty:", isGetTableStatusEmpty);
    const existingTables = await db.tableData.toArray();
    console.log("[BUG-1499] existing DB tables:", existingTables.map((t) => ({ id: t.cwrFbTableId, name: t.name || t.title, statusType: t.statusType, tabbleSync: t.tabbleSync })));
    const existingStatusMap = new Map(existingTables.map((t) => [t.cwrFbTableId, t]));
    tempData.forEach((t) => {
      if (t.statusType === "OPN") {
        const existing = existingStatusMap.get(t.cwrFbTableId);
        if (existing && existing.statusType !== "OPN") {
          const willPreserve = isGetTableStatusEmpty || existing.tabbleSync !== "Y";
          console.log("[BUG-1499] Table candidate for preservation:", {
            tableId: t.cwrFbTableId,
            name: t.name || t.title,
            serverStatus: "OPN",
            localStatus: existing.statusType,
            localTabbleSync: existing.tabbleSync,
            isGetTableStatusEmpty,
            willPreserve,
          });
          if (willPreserve) {
            t.statusType = existing.statusType;
            t.color = existing.color;
            t.noOfPersons = existing.noOfPersons;
            t.guestName = existing.guestName;
            t.guestType = existing.guestType;
            t.referredBy = existing.referredBy;
            t.fbtableStatusId = existing.fbtableStatusId;
            t.waiter = existing.waiter;
            t.tabbleSync = existing.tabbleSync;
            // merge/splitTables/isSplitParent intentionally not restored —
            // those are only valid once the server has committed them via getTableStatus.
          }
        }
      }
    });

    // Cross-reference against all active FB orders (status "IP") to detect tables
    // where the server's tableStatus record is OPN but an fbOrder is still in
    // progress. This catches data inconsistency that neither getTableStatus nor the
    // preservation block above can recover from (both sides show OPN). Marking
    // tabbleSync:"N" queues the table for the retry block below, which calls
    // upsertTableStatus(OCU) to permanently fix the server record as well.
    try {
      const activeFbOrderResponse = await retailInstance({
        data: {
          query: `query { getFBOrder(tableId: null status: "IP") { fbTableId isCanceled guests guestName guestType referredBy salesRepId } }`,
        },
      });
      const activeOrders = activeFbOrderResponse?.data?.data?.getFBOrder || [];
      const activeOrderMap = new Map();
      activeOrders
        .filter((o) => o.isCanceled !== "Y")
        .forEach((o) => {
          if (o.fbTableId) activeOrderMap.set(o.fbTableId, o);
        });

      if (activeOrderMap.size > 0) {
        tempData.forEach((t) => {
          const activeOrder = activeOrderMap.get(t.cwrFbTableId);
          if (t.statusType === "OPN" && activeOrder) {
            // Only correct tables where tabbleSync is undefined — these are records
            // that were wiped by the old sync bug before this fix was deployed.
            // Skip when tabbleSync has any value set by our managed flow:
            //   "N" = upsertTableStatus(OPN) in flight (payment being confirmed)
            //   "Y" = server confirmed OPN (table intentionally freed)
            // Correcting either causes the retry block to call upsertTableStatus(OCU)
            // while payment calls upsertTableStatus(OPN), producing the OCU↔OPN
            // WebSocket oscillation seen as "Order Placed with no items" after payment.
            const localTable = existingStatusMap.get(t.cwrFbTableId);
            if (localTable && localTable.statusType === "OPN" && localTable.tabbleSync != null) {
              console.log("[BUG-1499] Cross-reference skipped — tabbleSync:", localTable.tabbleSync, "for:", t.name || t.cwrFbTableId);
              return;
            }
            console.log("[BUG-1499] Mismatch corrected — OPN table has active IP order:", t.name || t.cwrFbTableId);
            t.statusType = "OCU";
            t.color = "#bc4749";
            if (activeOrder.guests) t.noOfPersons = activeOrder.guests;
            if (activeOrder.guestName) t.guestName = activeOrder.guestName;
            if (activeOrder.guestType) t.guestType = activeOrder.guestType;
            if (activeOrder.referredBy) t.referredBy = activeOrder.referredBy;
            if (activeOrder.salesRepId) t.waiter = activeOrder.salesRepId;
            t.tabbleSync = "N";
          }
        });
      }
    } catch (e) {
      console.debug("[BUG-1499] FB order cross-reference skipped (non-fatal):", e);
    }

    await db.tableData.clear();
    await db.tableData.bulkPut(tempData);

    // Retry any table status updates that never reached the server (tabbleSync: "N").
    // These are tables whose upsertTableStatus API call failed — they were written
    // optimistically to local DB and preserved through the sync above. Now that we
    // have a working connection (sync just succeeded), re-attempt those calls.
    const pendingTables = tempData.filter((t) => t.tabbleSync === "N" && t.statusType !== "OPN");
    if (pendingTables.length > 0) {
      const syncedTableIds = new Set();
      for (const pending of pendingTables) {
        if (!pending.cwrFbTableId || !pending.cwrFbsectionId) continue;
        try {
          const mergeStr =
            Array.isArray(pending.merge) && pending.merge.length > 0
              ? "[" +
                pending.merge
                  .map((m) => `{fbMergeId:"${m.fbMergeId || ""}" fbMTableId:"${m.fbMTableId || ""}" fbMergeReason:"${m.fbMergeReason || ""}"}`)
                  .join(",") +
                "]"
              : "[]";
          const splitStr =
            Array.isArray(pending.splitTables) && pending.splitTables.length > 0
              ? "[" +
                pending.splitTables
                  .map(
                    (s) => `{
                    splitTable:"${s.splitTable || ""}"
                    status:"${s.status || "OPN"}"
                    guests:${s.guests || 0}
                    guestName:"${s.guestName || ""}"
                    guestType:"${s.guestType || ""}"
                    ${s.salesRepId ? `salesRepId:"${s.salesRepId}"` : ""}
                  }`
                  )
                  .join(",") +
                "]"
              : "[]";
          const retryParams = {
            query: `mutation {
              upsertTableStatus(tableStatus: {
                cSBunitID: "${tillData.tillAccess.csBunit.csBunitId}"
                fbtableStatusId: ${pending.fbtableStatusId ? `"${pending.fbtableStatusId}"` : null}
                fbTableId: "${pending.cwrFbTableId}"
                fbSectionId: "${pending.cwrFbsectionId}"
                status: "${pending.statusType}"
                guests: ${pending.noOfPersons || 0}
                guestName: "${pending.guestName || ""}"
                guestType: "${pending.guestType || ""}"
                referredBy: "${pending.referredBy || ""}"
                salesRepId: ${pending.waiter ? `"${pending.waiter}"` : null}
                merge: ${mergeStr}
                splitTables: ${splitStr}
              }) { status message }
            }`,
          };
          const retryResponse = await upsertTableStatus(retryParams);
          if (retryResponse?.data?.data?.upsertTableStatus?.status === "200") {
            syncedTableIds.add(pending.cwrFbTableId);
          }
        } catch (retryErr) {
          console.debug("Retry upsertTableStatus failed for table:", pending.cwrFbTableId, retryErr);
        }
      }
      if (syncedTableIds.size > 0) {
        const refreshed = await db.tableData.toArray();
        const updated = refreshed.map((t) =>
          syncedTableIds.has(t.cwrFbTableId) ? { ...t, tabbleSync: "Y" } : t
        );
        await db.tableData.clear();
        await db.tableData.bulkPut(updated);
      }
    }

    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
};

const fetchRestaurantTables = async (tillId) => {
  try {
    const query = `query {
      getRestaurantTables(tillId: "${tillId}") {
        cwrFbsectionId
        sectionName
        posTables {
          cwrFbTableId
          cSBunitID
          cSClientID
          created
          createdby
          csWindowId
          isactive
          updated
          updatedby
          name
          capacity
          cwrFbFloorId
        }
      }
    }`;

    const response = await retailInstance({
      data: { query },
    });

    return response?.data?.data?.getRestaurantTables;
  } catch (error) {
    console.debug("Error in fetchRestaurant tables", error);
  }
};

const processRestaurantTables = (getRestaurantTables) => {
  let tempData = [];
  let sectionData = [{ value: "all", name: "All" }];

  getRestaurantTables.forEach((section, index) => {
    section.posTables.forEach((table) => {
      table.title = table.name;
      table.merge = true;
      table.cwrFbsectionId = section.cwrFbsectionId;
      table.sectionName = section.sectionName;
      table.table = table.name;
      table.color = "#a7c957";
      table.statusType = "OPN";
      table.originalIndex = index;
      tempData.push(table);
    });
    sectionData.push({ value: section.cwrFbsectionId, name: section.sectionName });
  });

  return { tempData, sectionData };
};

const fetchOccupiedTablesData = async () => {
  try {
    const query = `query {
      getTableStatus(tableId: null) {
        cSBunitID
        cSClientID
        isactive
        fbTableId
        fbSectionId
        guestName
        guestType
        referredBy
        status
        salesRepId
        guests
        fbtableStatusId
          merge {
      fbMergeId
      fbMergeReason
      fbMTableId
    }
        splitTables {
        isactive
        fbTableId
        fbSectionId
        guestName
        guestType
        referredBy
        status
        salesRepId
        guests
        fbtableStatusId
        splitTable
      }
      }
    }`;

    const response = await retailInstance({
      data: { query },
    });

    const result = response?.data?.data?.getTableStatus || [];
    console.log("[BUG-1499] getTableStatus response — total occupied tables:", result.length);
    console.log("[BUG-1499] getTableStatus fbTableIds returned:", result.map((t) => t.fbTableId));
    console.log("[BUG-1499] getTableStatus full data:", JSON.stringify(result, null, 2));
    return result;
  } catch (error) {
    console.error(error, "Error in fetch occupied tables");
  }
};

const updateTableDataWithOccupiedInfo = (tempData, occupiedData) => {
  occupiedData.forEach((occupied) => {
    const table = tempData.find((t) => t.cwrFbTableId === occupied.fbTableId);
    if (table) {
      table.color = getColorByStatus(occupied.status);
      table.noOfPersons = occupied.guests;
      table.guestName = occupied.guestName;
      table.guestType = occupied.guestType;
      table.referredBy = occupied.referredBy;
      table.fbtableStatusId = occupied.fbtableStatusId;
      table.statusType = occupied.status;
      table.waiter = occupied.salesRepId;
      table.merge = occupied.merge;
      table.splitTables = occupied.splitTables;
      table.isSplitParent = Array.isArray(occupied.splitTables) && occupied.splitTables.length > 0 ? "Y" : "N";
    }
  });
};

const getColorByStatus = (status) => {
  switch (status) {
    case "OCU":
      return "#bc4749";
    case "RES":
      return "#f2e8cf";
    case "OPN":
    default:
      return "#a7c957";
  }
};

const upsertTableStatus = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in upsert table status");
  }
};

const cancelFBOrder = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in cancel fbo order");
  }
};

const upsertTableReservation = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in upsert table reservation");
  }
};

const upsertFBOrder = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in upsert fbo order");
  }
};

const getFBOrder = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error("Error in getFBOrder", error);
  }
};

const insertFbOrdersToDB = async (orders = []) => {
  if (!orders.length) return;

  const now = new Date().toISOString();

  const fbOrderIds = orders.map((o) => o.fbOrderId);

  const existingOrders = await db.fbOrderData.where("fbOrderId").anyOf(fbOrderIds).toArray();

  const existingIdsSet = new Set(existingOrders.map((o) => o.fbOrderId));

  const newOrders = orders.filter((o) => !existingIdsSet.has(o.fbOrderId));

  if (!newOrders.length) return;

  const records = newOrders.map((order) => {
    const cartMeta = order.meta?.find((m) => m.key === "cartDetails");

    let wsData = {};
    try {
      wsData = cartMeta?.value ? JSON.parse(cartMeta.value) : {};
    } catch (e) {
      console.error("Invalid cartDetails JSON", e);
    }

    return {
      fbOrderId: order.fbOrderId,
      fbTableId: order.fbTableId,
      fbOrderStatus: order.status,
      fbOrderSync: "Y",
      cSBunitID: order.cSBunitID,
      orderNo: order.orderNo,
      salesRepId: order.salesRepId,
      guestName: order.guestName,
      isCanceled: order.isCanceled,
      createdTime: now,
      updatedTime: now,
      cart: wsData,
      meta: order.meta || [],
      lines: order.lines || [],
    };
  });

  await db.fbOrderData.bulkPut(records);
};

const getFBOrdersData = async () => {
  try {
    const paramsInput = {
      query: `query {
          getFBOrder(
            tableId: null
            status: "IP"
          ) {
            fbOrderId
          cSBunitID
          isactive
          sOrderId
          fbTableId
          orderNo
          fbSectionId
          date
          guests
          guestType
          salesRepId
          guestName
          referredBy
          status
          isCanceled
          discAmount
          fbSplitTable
          merge {
            fbMergeId
            fbMergeReason
            fbTableId
          }
          meta {
            fbOrderMetaId
            key
            value
          }
          lines {
            fbOrderLineId
            line
            mPoductId
            mBatchId
            description
            csUomId
            qty
            unitPrice
            listPrice
            csTaxId
            lineTax
            lineGross
            isOrdered
            sOrderLineId
            isCanceled
            discount
            mPricingRuleId
            name
            value
            grossStd
            grossUnit
            lineStd
            netList
            netStd
            netUnit
            unitTax
            meta {
              fbOrderLineMetaId
              key
              value
            }
          }
          }
        }`,
    };
    const response = await getFBOrder(paramsInput);
    console.debug("response ------>", response);
    const orders = response?.data?.data?.getFBOrder || [];
    console.debug("orders -------->", orders);
    await insertFbOrdersToDB(orders);
  } catch (error) {
    console.error("[SYNC] Error syncing FB orders", error);
  }
};

const getTableReservation = async (tableId, date) => {
  try {
    const tableArg = tableId ? `tableId: "${tableId}",` : "";

    const queryPayload = {
      query: `query {
        getTableReservation(${tableArg} date: "${date}") {
          cSBunitID
          cSClientID
          created
          createdby
          csWindowId
          isactive
          updated
          updatedby
          fbTableReservationId
          fbTableId
          fbSectionId
          customerName
          noOfPeople
          contactNo
          guestType
          referredBy
          resDate
          resTime
          reservationTime
          reservationDate
          status
          specialrequests
        }
      }`,
    };

    const response = await retailInstance({ data: queryPayload });
    return response?.data?.data?.getTableReservation || [];
  } catch (err) {
    console.error("Error fetching table reservations:", err);
    return [];
  }
};

const getRestaurantTables = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get restaurant tables");
  }
};

const productionHistory = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in production history");
  }
};

const upsertBOMProduction = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in upsert bom production");
  }
};

const upsertProductionRMIssue = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in upsert production rm issue");
  }
};

export {
  syncRestaurantTables,
  fetchRestaurantTables,
  processRestaurantTables,
  fetchOccupiedTablesData,
  updateTableDataWithOccupiedInfo,
  getColorByStatus,
  upsertTableStatus,
  cancelFBOrder,
  upsertTableReservation,
  upsertFBOrder,
  getFBOrder,
  insertFbOrdersToDB,
  getFBOrdersData,
  getTableReservation,
  getRestaurantTables,
  productionHistory,
  upsertBOMProduction,
  upsertProductionRMIssue,
};
